import usePrevious from 'hooks/usePrevious';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ADD_DELETE_REQUEST, REMOVE_DELETE_REQUEST, UPDATE_APPLICATION_DETAIL_POST_DATA } from 'store/key';
import { generateRandomId, produce } from 'utility/general';
import { isEqual } from 'lodash';

const useCollaboratingSiteData = ({ formId = -1, siteInfoId = -1 }) => {
    const [openPopup, setOpenPopup] = useState(false);
    const dispatch = useDispatch();
    const serverTableKey = 'studySiteCollab';
    const remoteData = useSelector((state) => state.applicationDetail?.[formId]?.[serverTableKey]);
    const updatePostData = useSelector((state) => state.applicationDetailPostData.postData[serverTableKey]);
    const [rows, setRows] = useState(remoteData ?? []);
    const previousData = usePrevious(remoteData);
    const [isEdit, setEdit] = useState(false);

    const [dialogData, setDialogData] = useState({});

    const onDialogDataUpdate = (e, clearFields = []) => {
        const { value, name } = e.target;

        setDialogData(
            produce((draft) => {
                draft[name] = value;

                clearFields?.forEach((item) => {
                    delete draft[item];
                });
            }),
        );
    };

    const removeAllTempData = () => {
        setRows(
            rows.filter((item) => {
                return !item.id.toString().startsWith('temp_');
            }),
        );
    };

    const defaultFormList = {
        cluster: {
            value: dialogData['cluster'],
            label: 'Cluster',
            name: 'cluster',
            type: 'cluster',
            isRequired: true,
            disableOtherOption: true,
            onChange: (e) => {
                onDialogDataUpdate(e, ['hospitalCode']);
            },
        },
        hospitalCode: {
            value: dialogData.hospitalCode,
            label: 'Hospital',
            name: 'hospitalCode',
            type: 'hospitalOrInstitution',
            validators: ['isRequired'],
            onChange: onDialogDataUpdate,
            disabled: !dialogData.cluster,
            allowClusters: dialogData.cluster ? [dialogData.cluster] : [],
        },
        department: {
            value: dialogData.department,
            label: 'Department',
            name: 'department',
            type: 'input',
            onChange: onDialogDataUpdate,
            validators: ['isRequired'],
            maxLength: 255,
        },
        others: {
            value: dialogData.others,
            label: 'If others, please specify',
            name: 'others',
            type: 'input',
            onChange: onDialogDataUpdate,
            maxLength: 255,
        },
    };

    // update row when remote data update
    useEffect(() => {
        if (!isEqual(previousData, remoteData)) {
            setRows(remoteData);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [remoteData]);

    const getLabelName = (key) => {
        return defaultFormList[key]?.label ?? 'undefined';
    };

    const [columns] = useState([
        { field: 'cluster', headerName: getLabelName('cluster'), width: 120 },
        { field: 'hospitalCode', headerName: getLabelName('hospitalCode'), width: 150 },
        { field: 'department', headerName: getLabelName('department'), width: 150 },
        { field: 'others', headerName: getLabelName('others'), width: 300 },
    ]);

    const handleEditClick = (row) => {
        // setSelectedRow(row);
        setDialogData(row);
        setOpenPopup(true);
        setEdit(true);
    };

    const handleRowCreate = async (id) => {
        setDialogData({});
        setOpenPopup(true);
        setEdit(false);
    };

    const onRecordUpdate = async () => {
        const data = dialogData;
        data.id = `temp_${generateRandomId()}`;
        const newRows = produce(rows, (draft) => {
            draft.push(data);
        });
        setRows(newRows);
        dispatch({
            type: UPDATE_APPLICATION_DETAIL_POST_DATA,
            payload: {
                type: serverTableKey,
                data: [...(updatePostData ?? []), data],
                applicationFormId: formId,
            },
        });

        setOpenPopup(false);
    };

    const onEditSubmit = () => {
        const row = dialogData;

        let postData = updatePostData ?? [];

        const index = postData.findIndex((item) => {
            return item.id === row.id;
        });

        if (index === -1) {
            postData.push(row);
        } else {
            postData[index] = row;
        }

        dispatch({
            type: UPDATE_APPLICATION_DETAIL_POST_DATA,
            payload: {
                type: serverTableKey,
                data: postData,
                applicationFormId: formId,
            },
        });

        //update local data
        setRows(
            produce((draft) => {
                const index = draft.findIndex((item) => {
                    return item.id === row.id;
                });

                draft[index] = row;
            }),
        );

        setOpenPopup(false);
    };

    const recoverDataCreation = async (item) => {
        // handle edited data recovery
        const updatedPostData = [];

        rows.forEach((item) => {
            const remoteDataObject = remoteData.find((remoteItem) => {
                return remoteItem.id === item.id;
            });

            if (remoteDataObject) {
                const isSame = Object.entries(item).every(([key, value]) => {
                    // remove exception
                    if (key === 'siteInfo' || key === 'applicationForm') {
                        return true;
                    }

                    return remoteDataObject[key] === value;
                });

                if (!isSame) {
                    updatedPostData.push(item);
                }
            }
        });

        const postData = rows.filter((row) => {
            const isDataCreation = row.id.toString().startsWith('temp_');

            if (!isDataCreation) {
                dispatch({
                    type: REMOVE_DELETE_REQUEST,
                    payload: {
                        type: serverTableKey,
                        id: row.id,
                        applicationFormId: formId,
                    },
                });
            }

            return isDataCreation;
        });

        dispatch({
            type: UPDATE_APPLICATION_DETAIL_POST_DATA,
            payload: {
                type: serverTableKey,
                data: [...postData, ...updatedPostData],
                applicationFormId: formId,
            },
        });
    };

    const onDataEmpty = async (item) => {
        remoteData?.forEach((row) => {
            dispatch({
                type: ADD_DELETE_REQUEST,
                payload: {
                    type: serverTableKey,
                    id: row.id,
                    siteInfoId,
                    applicationFormId: formId,
                },
            });
        });

        dispatch({
            type: UPDATE_APPLICATION_DETAIL_POST_DATA,
            payload: {
                type: serverTableKey,
                data: [],
                applicationFormId: formId,
            },
        });
    };

    const handleDelete = async (row) => {
        setRows(
            produce((draft) => {
                const index = draft.findIndex((item) => {
                    return item.id === row.id;
                });

                if (index !== -1) {
                    draft.splice(index, 1);
                }
            }),
        );

        // skip if id starts with temp_, e.g. not exist in server
        if (row.id.toString().startsWith('temp_')) {
            let postData = updatePostData ?? [];

            const index = postData.findIndex((item) => {
                return item.id === row.id;
            });

            if (index !== -1) {
                postData.splice(index, 1);
            }

            dispatch({
                type: UPDATE_APPLICATION_DETAIL_POST_DATA,
                payload: {
                    type: serverTableKey,
                    data: postData,
                    applicationFormId: formId,
                },
            });

            return;
        }

        dispatch({
            type: ADD_DELETE_REQUEST,
            payload: {
                type: serverTableKey,
                id: row.id,
                siteInfoId,
                applicationFormId: formId,
            },
        });
    };

    return {
        columns,
        rows,
        setRows,
        openPopup,
        setOpenPopup,
        defaultFormList,
        handleRowCreate,
        onRecordUpdate,
        handleEditClick,
        handleDelete,
        onEditSubmit,
        onDataEmpty,
        recoverDataCreation,
        isEdit,
        removeAllTempData,
    };
};

export default useCollaboratingSiteData;
